// Light-weight email domain typo suggester (spec fork 25.6).
// Purpose : detect when a user types a slightly-misspelled common
// provider (e.g. "gmial.com" → "gmail.com") and offer a one-click
// correction. Implemented locally (no network call, no PII leak).
//
// Strategy : Levenshtein distance ≤ 2 against a curated list of the
// most common French / international providers. We deliberately do
// NOT suggest corrections when the typed domain already exists in
// the list (so "gmail.com" never gets "corrected" to "ymail.com").

const TOP_DOMAINS = [
  // French ISPs & webmails
  "gmail.com",
  "yahoo.fr",
  "yahoo.com",
  "hotmail.fr",
  "hotmail.com",
  "outlook.fr",
  "outlook.com",
  "live.fr",
  "live.com",
  "orange.fr",
  "wanadoo.fr",
  "free.fr",
  "sfr.fr",
  "laposte.net",
  "bbox.fr",
  "neuf.fr",
  "aol.com",
  "icloud.com",
  "me.com",
  "proton.me",
  "protonmail.com",
];

const TOP_DOMAINS_SET = new Set(TOP_DOMAINS);

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  // Two-row dynamic programming — O(n) memory.
  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,        // insertion
        prev[j] + 1,            // deletion
        prev[j - 1] + cost,     // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Suggest a corrected email when the typed domain looks like a typo
 * of one of the top providers. Returns `null` when no correction is
 * proposed (or when the email is incomplete / already valid).
 *
 * Examples :
 *   suggestEmail("foo@gmial.com")   → "foo@gmail.com"
 *   suggestEmail("foo@yahho.fr")    → "foo@yahoo.fr"
 *   suggestEmail("foo@gmail.com")   → null  (already valid)
 *   suggestEmail("foo")             → null  (incomplete)
 */
export function suggestEmail(raw) {
  if (typeof raw !== "string") return null;
  const v = raw.trim();
  if (!v || !v.includes("@")) return null;
  const at = v.lastIndexOf("@");
  const local = v.slice(0, at);
  const domain = v.slice(at + 1).toLowerCase();
  // Must look like a domain (at least one dot, no spaces).
  if (!local || !domain || !domain.includes(".") || /\s/.test(domain)) return null;
  // Already a known provider → don't second-guess the user.
  if (TOP_DOMAINS_SET.has(domain)) return null;
  let best = null;
  let bestDist = 3; // strict cutoff
  for (const candidate of TOP_DOMAINS) {
    // First-letter guard : real typos almost always preserve the very
    // first character of the domain ("gmial" vs "gmail", "yhaoo" vs
    // "yahoo"). Rejecting candidates that differ on letter 0 kills the
    // « bar.fr → sfr.fr » class of false positives.
    if (domain[0] !== candidate[0]) continue;
    const d = levenshtein(domain, candidate);
    if (d > 0 && d < bestDist) {
      bestDist = d;
      best = candidate;
    }
  }
  if (!best) return null;
  // Tighten threshold for short domains to avoid borderline matches.
  if (domain.length <= 5 && bestDist >= 2) return null;
  return `${local}@${best}`;
}

/**
 * Returns just the corrected domain part (e.g. "gmail.com" from
 * "foo@gmial.com") — handy when the caller wants to render the
 * suggestion compactly.
 */
export function suggestEmailDomain(raw) {
  const full = suggestEmail(raw);
  if (!full) return null;
  return full.slice(full.lastIndexOf("@") + 1);
}
